import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "@/data/site";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const ProjectCard = ({ p, i }) => {
  const { lang } = useI18n();
  const title = lang === "en" ? p.title_en : p.title;
  const desc = lang === "en" ? p.desc_en : p.desc;
  return (
  <motion.article
    data-testid={`project-card-${i}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: EASE, delay: (i % 2) * 0.1 }}
    className={`group relative col-span-12 overflow-hidden border border-white/10 ${p.span}`}
  >
    <div className="relative h-72 overflow-hidden sm:h-80 lg:h-[26rem]">
      <img
        src={p.img}
        alt={title}
        className="h-full w-full object-cover opacity-55 grayscale transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-80 group-hover:grayscale-0"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
    </div>

    <div className="absolute inset-x-0 bottom-0 p-7 sm:p-8">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-neon">
        {p.cat}
      </span>
      <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
        {title}
      </h3>
      <p className="mt-2 max-w-md font-body text-sm leading-relaxed text-white/60 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
        {desc}
      </p>
    </div>

    <span className="absolute right-6 top-6 grid h-11 w-11 place-items-center border border-white/15 bg-black/40 text-white/70 backdrop-blur-sm transition-colors duration-300 group-hover:border-cyan-neon group-hover:text-cyan-neon">
      <ArrowUpRight size={18} />
    </span>
  </motion.article>
  );
};

export const Portfolio = () => {
  const { t } = useI18n();
  return (
    <section
      id="proiecte"
      data-testid="portfolio-section"
      className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32"
    >
      <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <div className="max-w-2xl">
          <Reveal>
            <SectionLabel index="[ 03 ]">{t("pf.label")}</SectionLabel>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("pf.h1")}{" "}
              <span className="text-cyan-neon">{t("pf.hkw")}</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="max-w-xs font-body text-sm leading-relaxed text-white/50">
            {t("pf.sub")}
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-12 gap-5">
        {PROJECTS.map((p, i) => (
          <ProjectCard key={i} p={p} i={i} />
        ))}
      </div>
    </section>
  );
};
